/**
 * Developer-owned UI customization per class (keyed by exact class title).
 */

import { getSpanishClassContent } from './classContentEs';
import { EIP_APPLICATION_SITE, EIP_APPLICATION_URL } from './eipConfig';

export const isSpanishLocale = (locale) => locale?.startsWith('es');

export const getVirtualClassLabel = (locale) =>
  isSpanishLocale(locale) ? 'Zoom (Clase virtual)' : 'Zoom (Virtual Class)';

export const IN_PERSON_ADDRESS = '1110 East 93rd Street, Brooklyn, NY 11216';

const formatInPersonLocation = (locale, address) => {
  const clean = (address || IN_PERSON_ADDRESS).replace(/^(In person|Presencial)\s*[—–-]\s*/i, '').trim();
  return isSpanishLocale(locale) ? `Presencial — ${clean}` : `In person — ${clean}`;
};

export const formatClassLocationDisplay = (classItem, locale, uiConfig = {}) => {
  if (!classItem) return '';
  if (classItem.location_type === 'zoom') {
    return getVirtualClassLabel(locale);
  }
  const address = classItem.location_details || uiConfig.locationLabel || IN_PERSON_ADDRESS;
  return formatInPersonLocation(locale, address);
};

export const formatSessionLocationDisplay = (session, locale, fallbackDetails) => {
  if (session?.location_type === 'zoom') {
    return getVirtualClassLabel(locale);
  }
  const address = session?.session_location || session?.location_details || fallbackDetails || IN_PERSON_ADDRESS;
  return formatInPersonLocation(locale, address);
};

export const localizeClassItem = (classItem, locale) => {
  if (!classItem || !isSpanishLocale(locale)) return classItem;

  const es = getSpanishClassContent(classItem.title);
  if (!es) return classItem;

  return {
    ...classItem,
    title: es.title || classItem.title,
    description: es.description || classItem.description,
    location_details: es.location_details || classItem.location_details,
    prerequisites: es.prerequisites || classItem.prerequisites,
    materials_needed: es.materials_needed || classItem.materials_needed,
  };
};

export const getLocalizedUiConfig = (title, locale) => {
  const config = getClassUiConfig(title);
  if (!isSpanishLocale(locale)) return config;

  const es = getSpanishClassContent(title);
  if (!es) return config;

  return {
    ...config,
    displayDuration: es.displayDuration || config.displayDuration,
    heroSubtitle: es.heroSubtitle || config.heroSubtitle,
    locationLabel: es.locationLabel || config.locationLabel,
    sections: es.sections || config.sections,
    onSiteTraining: es.onSiteTraining || config.onSiteTraining,
  };
};

export const SESSION_LANGUAGE_LABELS = {
  english: 'English',
  spanish: 'Spanish',
};

const EIP_HELP_SECTION = {
  title: 'Need Help to Pay for Training?',
  content:
    `You may qualify for an EIP scholarship from the Educational Incentive Program, funded by the Office of Children and Family Services. To request an application you can call (800) 295-9616, or visit ${EIP_APPLICATION_SITE}`,
  link: EIP_APPLICATION_URL,
  linkLabel: 'Visit EIP application site',
};

export const CLASS_UI_CONFIG = {
  'Child Development Associate (CDA)': {
    displayDuration: '120 hours · ~14 weeks',
    heroSubtitle: '120-hour credential preparation program via Zoom',
    locationLabel: 'Zoom (Virtual Class)',
    sections: [
      {
        title: 'Who Should Attend?',
        content:
          'Child Care Center Directors, Teachers, Assistant Teachers, Family Day Care providers, assistant providers, alternate providers, alternate assistants, and home visitors.',
      },
      {
        title: 'About the CDA Credential',
        content:
          'The CDA credential is administered by the Council for Professional Recognition in Washington D.C. Earning a CDA credential can help you improve your skills and confidence in working with children, and can also increase your opportunities for advancement in the Early Childhood field by helping you become eligible for positions such as Director, Group Leader, and Assistant Teacher.',
      },
      EIP_HELP_SECTION,
    ],
  },
  'Child Development and Learning (CDL)': {
    displayDuration: '30 hours',
    heroSubtitle: 'OCFS license renewal training via Zoom',
    locationLabel: 'Zoom (Virtual Class)',
    sections: [
      {
        title: 'Who Should Attend?',
        content:
          'Family Child Care or School Age Child Care staff who need to renew their license or registration.',
      },
      {
        title: 'Training Topics',
        content:
          'This class meets 30 hours training required by OCFS, within the ten training categories. For licensing, meet point and renewal.',
        numberedList: true,
        listItems: [
          'principles of childhood development, focusing on the developmental stages of the age groups for which the program provides care',
          'nutrition and health needs of infants and children',
          'child care program development',
          'safety and security procedures',
          'business record maintenance and management',
          'child abuse and maltreatment identification and prevention',
          'statutes and regulations pertaining to child care',
          'statutes and regulations pertaining to child abuse and maltreatment',
          'education and information on the identification, diagnosis and prevention of shaken baby syndrome',
          'adverse childhood experiences, focused on understanding trauma and on nurturing resiliency',
        ],
      },
      EIP_HELP_SECTION,
    ],
  },
  'Development and Operations': {
    displayDuration: '4 weeks',
  },
  'CPR and First Aid Certification': {
    displayDuration: '1 day',
  },
  'Health and Safety Competency Training': {
    displayDuration: '15 hours · 3 Saturdays',
    heroSubtitle: 'NY State mandated initial health and safety competencies for child care providers',
    locationLabel: IN_PERSON_ADDRESS,
    sections: [
      {
        title: 'Who Should Attend?',
        content:
          'Prospective childcare providers who are pursuing a Family or Group Family license or registration, existing childcare providers who are changing or renewing their license and have never taken the health and safety training before. Daycare Assistants may also take this class as part of their initial training requirements.',
      },
      {
        title: 'About This Training',
        content:
          'This is the State of New York mandated 15-hour training, competencies for becoming a family or group family child care provider. This training satisfies the required initial fifteen hours of training for new childcare employees and can also be taken as part of 30 Hour State Mandated Child Care Training. Seats for this class are limited. Pre-registration is required.',
      },
      {
        title: 'Schedule & Fees',
        content:
          'This is a three day course held at 1110 East 93rd St, Brooklyn, NY. Course fee: $300.',
      },
      EIP_HELP_SECTION,
    ],
  },
  'American Red Cross Adult and Pediatric First Aid/CPR/AED': {
    displayDuration: '1 day · 5 hours',
    heroSubtitle: 'American Red Cross certification — valid for two years',
    locationLabel: IN_PERSON_ADDRESS,
    sections: [
      {
        title: 'Who Should Attend?',
        content: 'Everyone who have children or work with children.',
      },
      {
        title: 'About This Certification',
        content:
          'This American Red Cross certified class will provide you the knowledge and skills necessary to deal with respiratory and cardiac emergencies in infants and children, until advanced medical personnel arrive and take charge. Participants will receive American Red Cross certification for two years.',
      },
      {
        title: 'Schedule & Fees',
        content:
          'Single-day classes held at 1110 East 93rd St, Brooklyn, NY from 10:00 AM to 3:00 PM. Course fee: $135. Pre-registration is required.',
      },
      EIP_HELP_SECTION,
    ],
    onSiteTraining: {
      title: 'On-Site Training for Businesses and Groups',
      intro:
        'An American Red Cross Certified Instructor will come to your program or group during weekdays, evenings or Saturdays.',
      listItems: [
        'Group of at least 5 participants is required.',
        'Call for more information',
      ],
    },
  },
  'Orientation for New Family Child Care Providers': {
    displayDuration: 'Free · 1 session',
    heroSubtitle: 'Free orientation for prospective family child care providers via Zoom',
    locationLabel: 'Zoom (Virtual Class)',
    sections: [
      {
        title: 'Who Should Attend?',
        content: 'Prospective family day care providers.',
      },
      {
        title: 'What You Will Learn',
        content: 'This orientation helps prospective family day care providers:',
        listItems: [
          'Understand the process involved in becoming a licensed child care provider.',
          'What is a family day care provider?',
          'Benefits of starting your own family child care business.',
          'Application process and requirements.',
          'Resources available to new and existing family child care providers.',
        ],
      },
      {
        title: 'Schedule & Fees',
        content:
          'Single-session orientation held via Zoom. This class is free. Pre-registration is required.',
      },
    ],
  },
};

const LANDING_SHOWCASE_STRIP_PATTERNS = [
  /\.?\s*The fee may be reimbursed through the SUNY EIP Rebate Program upon receiving a license or registration\. Rebate forms will be provided at the end of the Health and Safety training\.?/gi,
  /\.?\s*La tarifa puede reembolsarse a través del Programa de Reembolso SUNY EIP al recibir una licencia o registro\. Los formularios de reembolso se proporcionarán al final de la capacitación de Salud y Seguridad\.?/gi,
  /\.?\s*(Course fee|Tarifa del curso):\s*\$[\d,]+(?:\.\d+)?\.?/gi,
  /\.?\s*This class is free\.?/gi,
  /\.?\s*Esta clase es gratis\.?/gi,
  /\.?\s*Esta clase es gratuita\.?/gi,
  /\.?\s*Classes meet on Monday through Friday from 7:00 PM to 10:00 PM via Zoom\.?/gi,
  /\.?\s*Las clases se reúnen de lunes a viernes de 7:00 PM a 10:00 PM por Zoom\.?/gi,
  /\.?\s*Classes meet on Saturdays from 1:00 PM to 10:00 PM via Zoom\.?/gi,
  /\.?\s*Las clases se reúnen los sábados de 1:00 PM a 10:00 PM por Zoom\.?/gi,
  /\.?\s*Sessions are held via Zoom\.?/gi,
  /\.?\s*Las sesiones se realizan por Zoom\.?/gi,
  /\.?\s*This is a three[- ]day course held at 1110 East 93rd St, Brooklyn, NY\.?/gi,
  /\.?\s*This is a three-Saturday course held at 1110 East 93rd St, Brooklyn, NY\.?/gi,
  /\.?\s*Este es un curso de tres días en 1110 East 93rd St, Brooklyn, NY\.?/gi,
  /\.?\s*Este es un curso de tres sábados en 1110 East 93rd St, Brooklyn, NY\.?/gi,
  /\.?\s*Classes are held at 1110 East 93rd St, Brooklyn, NY\.?/gi,
  /\.?\s*Las clases se imparten en 1110 East 93rd St, Brooklyn, NY\.?/gi,
];

export const ORIENTATION_CLASS_TITLE = 'Orientation for New Family Child Care Providers';

export const isOrientationClass = (englishTitle) =>
  englishTitle === ORIENTATION_CLASS_TITLE;

export const getLandingShowcaseDescription = (description) => {
  if (!description) return '';

  let text = description.trim();
  for (const pattern of LANDING_SHOWCASE_STRIP_PATTERNS) {
    text = text.replace(pattern, '');
  }

  return text.replace(/\n{3,}/g, '\n\n').replace(/\.\s*\./g, '.').trim();
};

export const formatClassPrice = (price, locale) => {
  const amount = Number(price);
  if (!Number.isNaN(amount) && amount === 0) {
    return isSpanishLocale(locale) ? 'GRATIS' : 'FREE';
  }
  if (Number.isNaN(amount)) {
    return price != null && price !== '' ? String(price) : '';
  }
  return `$${amount}`;
};

export const getClassUiConfig = (title) => {
  if (!title) return {};
  return CLASS_UI_CONFIG[title] || {};
};

export const getDisplayDuration = (classItem, locale) => {
  const ui = getLocalizedUiConfig(classItem?.title, locale);
  return ui.displayDuration || classItem?.duration || '';
};

export const formatSessionLanguage = (language) => {
  if (!language) return SESSION_LANGUAGE_LABELS.english.toUpperCase();
  const label = SESSION_LANGUAGE_LABELS[language.toLowerCase()] || SESSION_LANGUAGE_LABELS.english;
  return label.toUpperCase();
};
